/**
 * Image field for the reading editor: pick a file, upload it, hand the caller the object PATH to
 * persist (never the signed URL). Inline uploads are appended to `data.media`; remove/replace do
 * not prune — `buildSavePayload()` reconciles the manifest at save time.
 */
import { computed, ref, watch } from 'vue'

import type { ComputedRef, Ref } from 'vue'

import { classifyFiles } from '@shared/ui/other'

import { useAssignmentEditorStore } from './store'
import { useAssignmentTemplateUploadMediaApi } from '../../api/assignment-templates/upload-media.hook'
import {
  rememberAssignmentMediaLink,
  useAssignmentMediaSrc,
} from './media-asset.hook'

/** Images only, no audio. */
export const ASSIGNMENT_IMAGE_ACCEPT: string[] = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.svg']

/** 80 MB (`App\Constants\File::MAX_SIZE_KB`); independent of the assessment limit. */
const ASSIGNMENT_IMAGE_MAX_SIZE = 81920 * 1024

export interface UseAssignmentImageUploaderOptions {
  value: () => string | undefined
  onChange: (value: string) => void
  /**
   * Called when an upload STARTS; returns the committer for its result. An upload outlives the
   * moment it was started — the component may be reused for another block, or unmounted, before
   * the response lands — so the caller pins the target here rather than letting `onChange` resolve
   * it on completion. Without it the result goes through `onChange`.
   */
  bindChange?: () => (value: string) => void
  /** Uploads under the template's `cover/` sub-directory. */
  cover?: () => boolean
}

/** The `<img>` after `refSrc` is known: the signed link resolving is only half the wait. */
export type AssignmentImagePreviewState = 'loading' | 'loaded' | 'failed'

export interface UseAssignmentImageUploaderReturn {
  refSrc: ComputedRef<string>
  refIsUploaded: ComputedRef<boolean>
  refIsResolving: Ref<boolean>
  refHasImage: ComputedRef<boolean>
  /** Skeleton: the link is resolving, or resolved and the bytes are still on their way. */
  refIsPreviewLoading: ComputedRef<boolean>
  /** The `<img>` has painted. */
  refIsPreviewReady: ComputedRef<boolean>
  /** Nothing to paint: no link came back, or the browser could not decode what did. */
  refIsPreviewBlank: ComputedRef<boolean>
  handleImageLoad: () => void
  handleImageError: () => void
  /** Empty for an uploaded object. */
  refExternalUrl: ComputedRef<string>
  refFileName: ComputedRef<string>
  refIsUploading: Ref<boolean>
  refProgress: Ref<number>
  refError: ComputedRef<string | null>
  refCanUpload: ComputedRef<boolean>
  handleFiles: (files: File[]) => void
  handleUrlChange: (value: string) => void
  handleRemove: () => void
}

export const useAssignmentImageUploader = (
  { value, onChange, bindChange, cover }: UseAssignmentImageUploaderOptions,
): UseAssignmentImageUploaderReturn => {
  const store = useAssignmentEditorStore()

  const {
    refSrc,
    refIsUploaded,
    refIsResolving,
    refError: refResolveError,
  } = useAssignmentMediaSrc(value)

  const refIsUploading = ref<boolean>(false)
  const refProgress = ref<number>(0)
  const uploadError = ref<string | null>(null)

  const refValue = computed<string>(() => {
    const raw = value()

    return typeof raw === 'string' ? raw.trim() : ''
  })

  const refHasImage = computed<boolean>(() => refValue.value.length > 0)

  const previewState = ref<AssignmentImagePreviewState>('loading')

  // Every new src starts over; the `<img>` reports back through the handlers below.
  watch(refSrc, () => {
    previewState.value = 'loading'
  })

  const refIsPreviewLoading = computed<boolean>(() =>
    refIsResolving.value || (refSrc.value.length > 0 && previewState.value === 'loading'),
  )

  const refIsPreviewReady = computed<boolean>(() =>
    refSrc.value.length > 0 && previewState.value === 'loaded',
  )

  const refIsPreviewBlank = computed<boolean>(() =>
    !refIsResolving.value && (refSrc.value.length === 0 || previewState.value === 'failed'),
  )

  const handleImageLoad = (): void => {
    previewState.value = 'loaded'
  }

  const handleImageError = (): void => {
    previewState.value = 'failed'
  }

  const refExternalUrl = computed<string>(() => (refIsUploaded.value ? '' : refValue.value))

  const refFileName = computed<string>(() => refValue.value.split('/').pop() ?? '')

  const refCanUpload = computed<boolean>(() => store.getTemplateId !== null)

  // Upload failures win over resolve errors.
  const refError = computed<string | null>(() => uploadError.value ?? refResolveError.value)

  const handleFiles = (files: File[]): void => {
    const file = files[0]
    if (!file || refIsUploading.value) return

    const { accepted, rejected } = classifyFiles([file], {
      accept: ASSIGNMENT_IMAGE_ACCEPT,
      maxSize: ASSIGNMENT_IMAGE_MAX_SIZE,
      remainingSlots: 1,
      existingKeys: new Set<string>(),
    })

    const refusal = rejected[0]

    if (refusal) {
      uploadError.value = refusal.reason
      return
    }

    const accept = accepted[0]
    if (!accept) return

    const templateId = store.getTemplateId

    if (templateId === null) {
      uploadError.value = 'Save the assignment before uploading images.'
      return
    }

    uploadError.value = null

    const isCover = cover?.() ?? false
    // Bound now, not on success: see `bindChange`.
    const commit = bindChange?.() ?? onChange

    void useAssignmentTemplateUploadMediaApi({
      templateId,
      file: accept.file,
      cover: isCover,
      onStart: () => {
        refIsUploading.value = true
        refProgress.value = 0
      },
      onProgress: (percent) => {
        refProgress.value = percent
      },
      onSuccess: (media) => {
        rememberAssignmentMediaLink(media)
        // A cover is a row column (`cover_path`), not part of the `data.media` manifest.
        if (!isCover) {
          store.addMediaPath(media.path)
        }
        // The path, never `media.url` — signed URLs are render-only.
        commit(media.path)
      },
      onError: (message) => {
        uploadError.value = message
      },
      onFinish: () => {
        refIsUploading.value = false
      },
    })
  }

  /** The replaced object stays in the bucket: no delete endpoint, and a duplicate may share it. */
  const handleUrlChange = (next: string): void => {
    uploadError.value = null
    onChange(next.trim())
  }

  const handleRemove = (): void => {
    uploadError.value = null
    onChange('')
  }

  return {
    refSrc,
    refIsUploaded,
    refIsResolving,
    refHasImage,
    refIsPreviewLoading,
    refIsPreviewReady,
    refIsPreviewBlank,
    handleImageLoad,
    handleImageError,
    refExternalUrl,
    refFileName,
    refIsUploading,
    refProgress,
    refError,
    refCanUpload,
    handleFiles,
    handleUrlChange,
    handleRemove,
  }
}
